// src/pages/customer-panel/courses/MyCoursesPage.js
// Student's enrolled courses page

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    LinearProgress,
    Button,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Chip,
    Paper,
    Skeleton,
    Avatar,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const TAB_LIST = [
    { label: 'All Courses', value: 'all' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
];

const CARD_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

function getGradient(index) {
    return CARD_GRADIENTS[index % CARD_GRADIENTS.length];
}

const MyCourseCard = ({ course, index }) => {
    const navigate = useNavigate();
    const progress = course.progress || course.completion_percentage || 0;
    const isCompleted = progress >= 100 || course.status === 'completed';
    const courseId = course.slug || course.course_id || course.id || course._id;

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                    borderColor: ACCENT,
                },
            }}
            onClick={() => navigate(`/customer-panel/courses/${courseId}`)}
        >
            {/* Course header gradient */}
            <Box
                sx={{
                    height: 120,
                    background: course.thumbnail
                        ? `url(${course.thumbnail}) center/cover`
                        : getGradient(index),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}
            >
                {!course.thumbnail && (
                    <SchoolIcon sx={{ fontSize: 42, color: 'rgba(255,255,255,0.7)' }} />
                )}
                {isCompleted && (
                    <Chip
                        icon={<CheckCircleIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                        label="Completed"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: ACCENT,
                            color: 'white',
                            fontFamily,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                {/* Title */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                    }}
                >
                    {course.title || course.course_title || 'Untitled Course'}
                </Typography>

                {/* Instructor */}
                {(course.instructor || course.instructor_name) && (
                    <Typography
                        variant="caption"
                        sx={{ fontFamily, color: '#94a3b8', display: 'block', mb: 1.5 }}
                    >
                        by {course.instructor || course.instructor_name}
                    </Typography>
                )}

                {/* Meta info */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {(course.modules_count || course.total_modules) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AutoStoriesIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                {course.modules_count || course.total_modules} modules
                            </Typography>
                        </Box>
                    )}
                    {course.last_accessed && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                                {new Date(course.last_accessed).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Progress */}
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b', fontWeight: 500 }}>
                            Progress
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, fontWeight: 700, color: isCompleted ? ACCENT : '#475569' }}
                        >
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: isCompleted ? ACCENT : '#3b82f6',
                                borderRadius: 4,
                            },
                        }}
                    />
                </Box>

                {/* Completed modules */}
                {(course.completed_modules !== undefined && course.total_modules) && (
                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                        {course.completed_modules}/{course.total_modules} modules completed
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                <Button
                    fullWidth
                    variant={isCompleted ? 'outlined' : 'contained'}
                    startIcon={isCompleted ? <EmojiEventsIcon /> : <PlayArrowIcon />}
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customer-panel/courses/${courseId}`);
                    }}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '12px',
                        py: 1,
                        boxShadow: 'none',
                        ...(isCompleted
                            ? {
                                borderColor: ACCENT,
                                color: ACCENT,
                                '&:hover': { borderColor: '#059669', bgcolor: `${ACCENT}08` },
                            }
                            : {
                                bgcolor: ACCENT,
                                '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
                            }),
                    }}
                >
                    {isCompleted ? 'Review Course' : 'Continue Learning'}
                </Button>
            </CardActions>
        </Card>
    );
};

const CourseCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Skeleton variant="rectangular" height={120} />
        <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" sx={{ fontSize: '1.1rem', mb: 0.5 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem', mb: 2 }} />
            <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4 }} />
        </CardContent>
    </Card>
);

const EmptyState = ({ status }) => {
    let title = 'No courses yet';
    let description = 'You have not enrolled in any courses yet.';
    let icon = <SchoolIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />;

    if (status === 'in_progress') {
        title = 'No courses in progress';
        description = 'Start a course and it will appear here.';
        icon = <PlayArrowIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />;
    } else if (status === 'completed') {
        title = 'No completed courses';
        description = 'Complete a course to see it here. Keep learning!';
        icon = <EmojiEventsIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />;
    }

    return (
        <Paper
            sx={{
                py: 8,
                px: 3,
                textAlign: 'center',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
            }}
        >
            {icon}
            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mt: 2, mb: 1 }}>
                {title}
            </Typography>
            <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>{description}</Typography>
            <Button
                variant="contained"
                href="/customer-panel/courses"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    textTransform: 'none',
                    bgcolor: ACCENT,
                    borderRadius: '12px',
                    px: 4,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#059669' },
                }}
            >
                Browse Courses
            </Button>
        </Paper>
    );
};

export default function MyCoursesPage() {
    const navigate = useNavigate();

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('all');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const firstName = user?.profile?.fname || 'Student';

    const fetchCourses = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (activeTab !== 'all') params.status = activeTab;

            const response = await axiosInstance.get('/lms/portal/my-courses', { params });
            const data = response.data;
            setCourses(data.courses || data.data || data.results || []);
        } catch (err) {
            console.error('Failed to fetch my courses:', err);
            setError(err.response?.data?.message || 'Failed to load your courses.');
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const handleTabChange = useCallback((_, newValue) => {
        setActiveTab(newValue);
    }, []);

    // Stats
    const totalCourses = courses.length;
    const inProgressCount = courses.filter(
        (c) => c.status === 'in_progress' || (c.progress > 0 && c.progress < 100)
    ).length;
    const completedCount = courses.filter(
        (c) => c.status === 'completed' || c.progress >= 100
    ).length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontFamily,
                        fontWeight: 800,
                        color: 'white',
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                >
                    My Courses
                </Typography>
                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                    Keep track of your learning journey, {firstName}
                </Typography>

                {/* Quick stats */}
                <Box sx={{ display: 'flex', gap: 3, mt: 2.5, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Enrolled', value: totalCourses, icon: SchoolIcon },
                        { label: 'In Progress', value: inProgressCount, icon: PlayArrowIcon },
                        { label: 'Completed', value: completedCount, icon: CheckCircleIcon },
                    ].map((stat) => (
                        <Box
                            key={stat.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: '12px',
                                px: 2,
                                py: 1,
                            }}
                        >
                            <stat.icon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 20 }} />
                            <Typography sx={{ fontFamily, color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                                {stat.value}
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                                {stat.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Main Content */}
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Tabs */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        mb: 3,
                        overflow: 'hidden',
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            px: 1,
                            '& .MuiTab-root': {
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                minHeight: 52,
                                color: '#64748b',
                                '&.Mui-selected': { color: ACCENT },
                            },
                            '& .MuiTabs-indicator': {
                                bgcolor: ACCENT,
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                        }}
                    >
                        {TAB_LIST.map((tab) => (
                            <Tab key={tab.value} label={tab.label} value={tab.value} />
                        ))}
                    </Tabs>
                </Paper>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchCourses} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <CourseCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : courses.length === 0 ? (
                    <EmptyState status={activeTab} />
                ) : (
                    <Grid container spacing={3}>
                        {courses.map((course, index) => (
                            <Grid item xs={12} sm={6} md={4} key={course.id || course._id || index}>
                                <MyCourseCard course={course} index={index} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
}
